import React, { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  Eye, 
  Crown, 
  Star, 
  User, 
  Building2, 
  Check, 
  X,
  Mic,
  Brain,
  Download,
  Trophy,
  Flame,
  Smile,
  ChevronDown,
  ChevronUp,
  Save,
  ToggleLeft,
  ToggleRight,
  Image,
  Link,
  Palette,
  Moon,
  Users,
  BarChart3,
  Gift,
  Bell,
  Megaphone,
  Share2,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Use same-origin API path on production custom domains
const API = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Icon mapping for tiers
const TIER_ICONS = {
  founding_member: Crown,
  premium: Star,
  free: User,
  enterprise: Building2
};

// Feature icons mapping - comprehensive list
const FEATURE_ICONS = {
  voice_typing: Mic,
  ai_coach: Brain,
  export_pdf: Download,
  badges: Trophy,
  streaks: Flame,
  mood_tracking: Smile,
  gamification: Trophy,
  image_attachments: Image,
  link_preview: Link,
  themes: Palette,
  dark_mode: Moon,
  client_admin_portal: Users,
  analytics_dashboard: BarChart3,
  sidebar_promo: Megaphone,
  main_promo_banner: Gift,
  push_notifications: Bell,
  referral_system: Share2
};

// Feature metadata with categories and descriptions
const FEATURE_CONFIG = {
  // Core Journal Features
  voice_typing: { label: 'Voice Typing', category: 'core', description: 'Microphone button in rich text editor' },
  ai_coach: { label: 'AI Coach Reflections', category: 'core', description: 'Get AI-powered insights on entries' },
  export_pdf: { label: 'PDF Export', category: 'core', description: 'Download and print journal entries' },
  mood_tracking: { label: 'Mood Tracking', category: 'core', description: 'Track and visualize mood patterns' },
  image_attachments: { label: 'Image Attachments', category: 'core', description: 'Add images to journal entries' },
  link_preview: { label: 'Link Preview', category: 'core', description: 'Preview links in entries' },
  
  // Gamification
  gamification: { label: 'Gamification', category: 'gamification', description: 'Enable all gamification features' },
  streaks: { label: 'Streaks', category: 'gamification', description: 'Track daily journaling streaks' },
  badges: { label: 'Badges', category: 'gamification', description: 'Earn achievement badges' },
  
  // Appearance
  themes: { label: 'Theme Customization', category: 'appearance', description: 'Customize app colors and styling' },
  dark_mode: { label: 'Dark/Light Mode', category: 'appearance', description: 'Toggle between dark and light themes' },
  
  // Premium Features
  client_admin_portal: { label: 'Client Admin Portal', category: 'premium', description: 'B2B client self-service portal' },
  analytics_dashboard: { label: 'Analytics Dashboard', category: 'premium', description: 'Advanced usage analytics' },
  
  // Promotional
  sidebar_promo: { label: 'Sidebar Promotions', category: 'promo', description: 'Show promotional sidebar banners' },
  main_promo_banner: { label: 'Banner Carousel', category: 'promo', description: 'Rotating promotional banners' },
  push_notifications: { label: 'Push Notifications', category: 'promo', description: 'Send push notifications to users' },
  referral_system: { label: 'Referral System', category: 'promo', description: 'User referral tracking' }
};

const CATEGORY_LABELS = {
  core: 'Core Journal Features',
  gamification: 'Gamification',
  appearance: 'Appearance',
  premium: 'Premium Features',
  promo: 'Promotional & Marketing'
};

const TierPreview = () => {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async (retryCount = 0) => {
    try {
      
      const res = await axios.get(`${API}/api/admin/tiers`, {
        withCredentials: true
      });
      const activeTiers = (res.data.tiers || []).filter(t => t.is_active);
      setTiers(activeTiers);
      if (activeTiers.length > 0 && !selectedTier) {
        // Default to founding_member if exists, otherwise first tier
        const defaultTier = activeTiers.find(t => t.key === 'founding_member') || activeTiers[0];
        setSelectedTier(defaultTier.key);
      }
    } catch (error) {
      // Retry up to 2 times for auth errors (cookie might not be set yet)
      if (error.response?.status === 401 && retryCount < 2) {
        setTimeout(() => fetchTiers(retryCount + 1), 1000);
        return;
      }
      // Only show error for non-auth errors
      if (error.response?.status !== 401) {
        console.error('Failed to fetch tiers:', error);
        toast.error('Failed to load tiers');
      }
    } finally {
      setLoading(false);
    }
  };

  const currentTier = tiers.find(t => t.key === selectedTier);
  const TierIcon = currentTier ? (TIER_ICONS[currentTier.key] || Star) : Star;

  // Get feature status for current tier
  const getFeatureStatus = (featureKey) => {
    if (!currentTier?.features) return { enabled: false, limit: 0 };
    const feature = currentTier.features.find(f => f.key === featureKey);
    if (!feature) return { enabled: false, limit: 0 };
    return {
      enabled: feature.enabled && (feature.limit === null || feature.limit > 0),
      limit: feature.limit
    };
  };

  // Toggle feature on/off
  const toggleFeature = async (featureKey) => {
    if (!currentTier) return;
    
    const updatedTiers = tiers.map(tier => {
      if (tier.key !== selectedTier) return tier;
      
      const existingFeature = tier.features?.find(f => f.key === featureKey);
      let updatedFeatures;
      
      if (existingFeature) {
        // Toggle existing feature
        updatedFeatures = tier.features.map(f => 
          f.key === featureKey 
            ? { ...f, enabled: !f.enabled, limit: f.enabled ? 0 : null }
            : f
        );
      } else {
        // Add new feature
        updatedFeatures = [
          ...(tier.features || []),
          { key: featureKey, name: FEATURE_CONFIG[featureKey]?.label || featureKey, enabled: true, limit: null }
        ];
      }
      
      return { ...tier, features: updatedFeatures };
    });
    
    setTiers(updatedTiers);
    setHasChanges(true);
  };

  // Save changes to backend
  const handleSave = async () => {
    if (!currentTier) return;
    
    setSaving(true);
    try {
      
      
      // Find the updated tier
      const tierToUpdate = tiers.find(t => t.key === selectedTier);
      
      await axios.put(`${API}/api/admin/tiers/${selectedTier}`, tierToUpdate, {
        withCredentials: true
      });
      
      toast.success(`${currentTier.name} features updated!`);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Enable all features for a tier (useful for Founding Members)
  const enableAllFeatures = () => {
    const allFeatureKeys = Object.keys(FEATURE_CONFIG);
    
    const updatedTiers = tiers.map(tier => {
      if (tier.key !== selectedTier) return tier;
      
      // Create features array with all features enabled
      const enabledFeatures = allFeatureKeys.map(featureKey => ({
        key: featureKey,
        name: FEATURE_CONFIG[featureKey]?.label || featureKey,
        enabled: true,
        limit: null // Unlimited
      }));
      
      return { ...tier, features: enabledFeatures };
    });
    
    setTiers(updatedTiers);
    setHasChanges(true);
    toast.success('All features enabled! Click "Save Changes" to apply.');
  };

  // Group features by category
  const getFeaturesByCategory = () => {
    const grouped = {};
    Object.entries(FEATURE_CONFIG).forEach(([key, config]) => {
      if (!grouped[config.category]) {
        grouped[config.category] = [];
      }
      grouped[config.category].push({ key, ...config });
    });
    return grouped;
  };

  const featuresByCategory = getFeaturesByCategory();

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
        <div className="flex items-center gap-3 animate-pulse">
          <RefreshCw className="w-6 h-6 text-[#4A0A66] animate-spin" />
          <div className="h-6 bg-[#4A0A66] rounded w-48"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
      {/* Header - Always visible */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#4A0A66]/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Eye className="w-6 h-6 text-[#0FFFE2]" />
          <div>
            <h3 className="text-lg font-semibold text-white">Feature Control Panel</h3>
            <p className="text-sm text-[#FDF2F8]/50">
              Toggle features on/off for each membership tier
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {selectedTier && currentTier && (
            <span 
              className="text-sm px-3 py-1 rounded-full font-medium"
              style={{ 
                backgroundColor: `${currentTier?.badge_color || '#df1283'}20`,
                color: currentTier?.badge_color || '#df1283'
              }}
            >
              Editing: {currentTier?.name}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#FDF2F8]/50" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#FDF2F8]/50" />
          )}
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t border-[#4A0A66] p-4 space-y-6">
          {/* Show loading/retry message if no tiers */}
          {tiers.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-[#4A0A66] mx-auto mb-3 animate-spin" />
              <p className="text-[#FDF2F8]/50">Loading tiers...</p>
              <button 
                onClick={() => fetchTiers(0)}
                className="mt-3 text-sm text-[#0FFFE2] hover:underline"
              >
                Click to retry
              </button>
            </div>
          ) : (
            <>
              {/* Tier Selector */}
              <div className="flex flex-wrap gap-2">
                {tiers.map(tier => {
                  const Icon = TIER_ICONS[tier.key] || Star;
                  const isSelected = selectedTier === tier.key;
                  return (
                    <button
                      key={tier.key}
                      onClick={() => {
                        if (hasChanges) {
                          toast.info('Save your changes first!');
                          return;
                        }
                        setSelectedTier(tier.key);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all min-w-fit whitespace-nowrap ${
                        isSelected 
                          ? 'ring-2 ring-offset-2 ring-offset-[#1a0527]'
                          : 'border-transparent hover:bg-[#4A0A66]/30'
                      }`}
                      style={{ 
                        backgroundColor: isSelected ? `${tier.badge_color}30` : 'transparent',
                        borderColor: isSelected ? tier.badge_color : 'transparent',
                        '--tw-ring-color': tier.badge_color
                      }}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: tier.badge_color }} />
                      <span className="text-white text-sm font-medium">{tier.name}</span>
                    </button>
                  );
                })}
              </div>

          {/* Feature Grid with Toggles */}
          {currentTier && (
            <div className="space-y-4">
              {/* Tier Info Banner */}
              <div 
                className="p-4 rounded-xl flex items-center gap-4"
                style={{ 
                  background: `linear-gradient(135deg, ${currentTier.badge_color}20 0%, transparent 100%)`,
                  borderLeft: `4px solid ${currentTier.badge_color}`
                }}
              >
                <TierIcon className="w-8 h-8" style={{ color: currentTier.badge_color }} />
                <div className="flex-1">
                  <h4 className="text-white font-semibold">{currentTier.name}</h4>
                  <p className="text-sm text-[#FDF2F8]/60">{currentTier.description}</p>
                </div>
                <div className="text-right">
                  {currentTier.price_monthly > 0 ? (
                    <>
                      <div className="text-white font-bold">${currentTier.price_monthly}/mo</div>
                      {currentTier.price_yearly > 0 && (
                        <div className="text-xs text-[#FDF2F8]/50">${currentTier.price_yearly}/year</div>
                      )}
                    </>
                  ) : (
                    <span 
                      className="font-bold px-3 py-1 rounded-full text-sm"
                      style={{ 
                        backgroundColor: `${currentTier.badge_color}20`,
                        color: currentTier.badge_color
                      }}
                    >
                      {currentTier.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Save Button and Enable All (for Founding Members) */}
              <div className="flex justify-between items-center">
                {currentTier.key === 'founding_member' && (
                  <Button 
                    onClick={() => enableAllFeatures()}
                    variant="outline"
                    className="border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10"
                    data-testid="enable-all-features-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Enable All Features
                  </Button>
                )}
                {!currentTier.key === 'founding_member' && <div />}
                {hasChanges && (
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
                    data-testid="save-tier-features-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </div>

              {/* Features by Category with Toggles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(featuresByCategory).map(([category, features]) => (
                  <div 
                    key={category}
                    className="bg-[#4A0A66]/20 rounded-xl p-4"
                  >
                    <h5 className="text-sm font-semibold text-[#FDF2F8]/70 mb-3 uppercase tracking-wide">
                      {CATEGORY_LABELS[category]}
                    </h5>
                    <div className="space-y-2">
                      {features.map(feature => {
                        const status = getFeatureStatus(feature.key);
                        const Icon = FEATURE_ICONS[feature.key];
                        
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer hover:bg-[#4A0A66]/30 ${
                              status.enabled ? 'bg-[#22c55e]/10' : 'bg-red-500/5'
                            }`}
                            onClick={() => toggleFeature(feature.key)}
                          >
                            {/* Toggle Switch */}
                            <button
                              className={`w-12 h-6 rounded-full relative transition-all ${
                                status.enabled 
                                  ? 'bg-[#22c55e]' 
                                  : 'bg-[#4A0A66]'
                              }`}
                            >
                              <div 
                                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                                  status.enabled ? 'left-7' : 'left-1'
                                }`}
                              />
                            </button>
                            
                            {/* Feature Info */}
                            <div className="flex items-center gap-2 flex-1">
                              {Icon && <Icon className="w-4 h-4 text-[#FDF2F8]/50" />}
                              <div>
                                <span className="text-sm text-white block">{feature.label}</span>
                                <span className="text-xs text-[#FDF2F8]/40">{feature.description}</span>
                              </div>
                            </div>
                            
                            {/* Status Badge */}
                            {status.enabled && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[#22c55e]/20 text-[#22c55e]">
                                {status.limit === null ? 'Unlimited' : `${status.limit} uses`}
                              </span>
                            )}
                            {!status.enabled && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                                Disabled
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* What Users See Section */}
              <div className="p-4 bg-[#0FFFE2]/10 border border-[#0FFFE2]/30 rounded-xl">
                <h5 className="text-[#0FFFE2] font-semibold mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  What {currentTier.name} Users Will See
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#22c55e] font-medium mb-2">Enabled Features:</p>
                    <ul className="text-[#FDF2F8]/70 space-y-1">
                      {getFeatureStatus('voice_typing').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          Microphone button in editor toolbar
                        </li>
                      )}
                      {getFeatureStatus('ai_coach').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          "Get Reflection" button on entries
                        </li>
                      )}
                      {getFeatureStatus('streaks').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          Streak counter on dashboard
                        </li>
                      )}
                      {getFeatureStatus('badges').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          Badge collection in sidebar
                        </li>
                      )}
                      {getFeatureStatus('mood_tracking').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          Mood insights and analytics
                        </li>
                      )}
                      {getFeatureStatus('export_pdf').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          Download and Print buttons
                        </li>
                      )}
                      {getFeatureStatus('themes').enabled && (
                        <li className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#22c55e]" />
                          Theme customization options
                        </li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <p className="text-red-400 font-medium mb-2">Hidden Features:</p>
                    <ul className="text-[#FDF2F8]/50 space-y-1">
                      {!getFeatureStatus('voice_typing').enabled && (
                        <li className="flex items-center gap-2">
                          <X className="w-3 h-3 text-red-400" />
                          Voice typing button hidden
                        </li>
                      )}
                      {!getFeatureStatus('ai_coach').enabled && (
                        <li className="flex items-center gap-2">
                          <X className="w-3 h-3 text-red-400" />
                          AI Coach section hidden
                        </li>
                      )}
                      {!getFeatureStatus('streaks').enabled && (
                        <li className="flex items-center gap-2">
                          <X className="w-3 h-3 text-red-400" />
                          Streak display hidden
                        </li>
                      )}
                      {!getFeatureStatus('badges').enabled && (
                        <li className="flex items-center gap-2">
                          <X className="w-3 h-3 text-red-400" />
                          Badges section hidden
                        </li>
                      )}
                      {!getFeatureStatus('mood_tracking').enabled && (
                        <li className="flex items-center gap-2">
                          <X className="w-3 h-3 text-red-400" />
                          Mood insights hidden
                        </li>
                      )}
                      {!getFeatureStatus('export_pdf').enabled && (
                        <li className="flex items-center gap-2">
                          <X className="w-3 h-3 text-red-400" />
                          PDF export hidden
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Info about Dark/Light Mode */}
              <div className="p-4 bg-[#FF7901]/10 border border-[#FF7901]/30 rounded-xl">
                <h5 className="text-[#FF7901] font-semibold mb-2">About Dark/Light Mode</h5>
                <p className="text-sm text-[#FDF2F8]/70">
                  When enabled, users can toggle between dark and light themes in their settings. 
                  This is separate from the custom theme colors - it affects the overall brightness 
                  and contrast of the interface. Currently, the journal uses a dark theme by default.
                </p>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TierPreview;
